// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import Field from "../Field";
import {
  FieldPreview as StyledFieldPreview,
  ButtonLink,
  ExpandedField as StyledExpandedField,
  NestedFieldContainer
} from "./styles";
import ChecklistPreview from "./ChecklistPreview";
import Icon from "src/icons";
import SkeletonLoader from "src/components/LoadingState";
import { fetchChatroomUpdates } from "src/actions/chatroom";
import { getBatchChecklistFieldValues } from "src/actions/checklist";
import { getIsEmbeddedFieldsLoading } from "src/reducers";
import { makeGetChecklistFieldStatuses } from "src/reducers/checklist";
import { dataStages } from "src/constants";

import type { State } from "src/types";

type Props = {
  fields: Array<number>,
  roomId: number,
  showFieldNames: boolean,
  expand: boolean,
  toggleExpand: Function,
  defaultFields?: ?Array<Object>
};

const NestedFields = ({
  fields,
  roomId,
  showFieldNames,
  expand,
  toggleExpand,
  defaultFields = []
}: Props) => {
  const dispatch = useDispatch();
  const hasFetchedDefault = useRef(false);
  const hasFetchedExpanded = useRef(false);
  const lastRoomId = useRef(roomId);

  if (lastRoomId.current !== roomId) {
    hasFetchedDefault.current = false;
    hasFetchedExpanded.current = false;
    lastRoomId.current = roomId;
  }

  const defaultFieldIds = useMemo(() => {
    return (defaultFields || [])
      .map(id => parseInt(id, 10))
      .filter(id => !isNaN(id));
  }, [defaultFields]);

  const roomIdString = String(roomId);

  // Fields that are marked open by default
  const isDefaultFieldsLoading = useSelector(({ app }) =>
    getIsEmbeddedFieldsLoading(app, roomIdString, defaultFieldIds)
  );

  // Fields that are opened manually
  const isExpandedFieldsLoading = useSelector(({ app }) =>
    getIsEmbeddedFieldsLoading(app, roomIdString, fields)
  );

  const getChecklistFieldStatuses = useMemo(
    () => makeGetChecklistFieldStatuses(),
    []
  );

  // Fetch status for fields
  const fieldStatuses = useSelector(({ app }: State) =>
    getChecklistFieldStatuses(app.checklist, roomIdString, fields)
  );

  useEffect(() => {
    if (!hasFetchedDefault.current && defaultFieldIds.length > 0) {
      dispatch(getBatchChecklistFieldValues(String(roomId), defaultFieldIds));
      hasFetchedDefault.current = true;
    }
  }, [dispatch, roomId, defaultFieldIds]);

  // Get already fetched field IDs
  const alreadyFetchedFieldIds = useMemo(() => {
    return fields.filter(
      fieldId => fieldStatuses[fieldId] === dataStages.fetched
    );
  }, [fields, fieldStatuses]);

  useEffect(() => {
    if (expand && !hasFetchedExpanded.current) {
      dispatch(fetchChatroomUpdates());

      // Only fetch fields that haven't been loaded yet
      const unfetchedFields = fields.filter(
        fieldId => !alreadyFetchedFieldIds.includes(fieldId)
      );

      if (unfetchedFields.length > 0) {
        dispatch(getBatchChecklistFieldValues(String(roomId), unfetchedFields));
      }

      hasFetchedExpanded.current = true;
    }

    if (!expand) {
      hasFetchedExpanded.current = false;
    }
  }, [expand, dispatch, roomId, fields, alreadyFetchedFieldIds]);

  if (!expand) {
    return (
      <NestedFieldContainer>
        {showFieldNames && (
          <StyledFieldPreview>
            {fields.map((fieldId: number) => (
              <ChecklistPreview
                key={fieldId}
                fieldId={fieldId}
                roomId={roomId}
              />
            ))}
          </StyledFieldPreview>
        )}
        <StyledExpandedField style={{ marginTop: 0 }}>
          {isDefaultFieldsLoading ? (
            <>
              {defaultFieldIds.map(fieldId => (
                <SkeletonLoader key={fieldId} type="checklistField" />
              ))}
            </>
          ) : (
            (defaultFields || []).map(
              fieldId =>
                parseInt(fieldId) && (
                  <Field
                    key={fieldId}
                    id={parseInt(fieldId)}
                    roomId={`${roomId}`}
                  />
                )
            )
          )}
        </StyledExpandedField>

        {fields.length !== defaultFields?.length && (
          <ButtonLink
            data-cy="expand-field-button"
            type="button"
            onClick={toggleExpand}
          >
            <span>{i18n.t(k.VIEW_ALL_FIELDS)}</span>
            <Icon
              type="down"
              style={{
                width: "0.6em",
                height: "0.6em"
              }}
            />
          </ButtonLink>
        )}
      </NestedFieldContainer>
    );
  }

  return (
    <NestedFieldContainer>
      <StyledExpandedField data-cy="expanded-fields">
        {fields.map((fieldId: number) => {
          const fieldStatus = fieldStatuses[fieldId];
          const isFieldLoaded = fieldStatus === dataStages.fetched;

          if (!isFieldLoaded && isExpandedFieldsLoading) {
            return <SkeletonLoader key={fieldId} type="checklistField" />;
          }

          return <Field key={fieldId} id={fieldId} roomId={`${roomId}`} />;
        })}
      </StyledExpandedField>
      <ButtonLink
        data-cy="expand-field-button"
        type="button"
        onClick={toggleExpand}
      >
        <span>{i18n.t(k.COLLAPSE)}</span>
        <Icon
          type="down"
          style={{
            transform: "rotate(180deg)"
          }}
        />
      </ButtonLink>
    </NestedFieldContainer>
  );
};

export default NestedFields;
